/**
 * The article narrator: one reader, Inception's own voice, that the listener
 * can talk to.
 *
 * It reads an article paragraph by paragraph in the rendered brand voice
 * (lib/narrator/brand-voice.ts, played by lib/narrator/hosted-voice.ts), each
 * paragraph rendered once by its first listener and then served from storage
 * to everyone. Languages the rendered voice does not read, and any session
 * where it fails twice in a row, fall back to the device's own voice.
 *
 * The listener can interrupt at any moment:
 *   - by holding the microphone button and asking (push to talk); a quick tap
 *     keeps it listening until they stop speaking;
 *   - by typing in the question field, which holds the reading while it has
 *     the focus and lets it go on if it is left empty.
 * The microphone is never open while the reader speaks: a natural voice from
 * the speakers came back through it as a "question" (2026-10-23), which no
 * text comparison can reliably tell from the listener. The question goes to
 * /api/articles/ask; while it is out the reader says it is looking ("Let me
 * check the article.", and once "Still looking" if it takes a while), the
 * answer is spoken after that line ends, the reader asks whether there is
 * anything else (and listens for the reply when the question was spoken),
 * and after a short silence it says "Back to the article." and picks up a
 * second before the point it stopped.
 *
 * Framework-free. The React side subscribes to snapshots (see
 * components/articles/article-narrator.tsx). Browser APIs are reached only
 * inside methods, so the module imports cleanly on the server.
 */

import type { LanguageType } from 'shared/src/types/languages.type';
import { HOSTED_VOICE_LANGUAGES, type NarratorLineKey } from './brand-voice';
import { looksLikeEcho, speechWords } from './echo-guard';
import { ClipPlayer, ClipSource, PcmStreamPlayer, type ClipRequest, type ReaderAnswer } from './hosted-voice';
import { pickNarratorVoice } from './pick-voice';
import {
  MAX_CHUNK_CHARS,
  MAX_RENDERED_CHUNK_CHARS,
  chunkBlocks,
  cleanForSpeech,
  collectArticleBlocks,
  splitSentences,
  type NarratorBlock,
  type NarratorChunk,
} from './segment';

export type NarratorPhase = 'idle' | 'reading' | 'paused' | 'listening' | 'thinking' | 'answering' | 'ended';
export type MicState = 'unsupported' | 'ready' | 'blocked';
export type NarratorNote = 'mic-blocked' | 'answer-failed' | 'voice-failed' | null;
export type VoiceEngine = 'rendered' | 'device';

export interface NarratorSnapshot {
  /** This language can be read here (rendered voice, or a device voice). */
  available: boolean;
  phase: NarratorPhase;
  /** Position in the article, in chunks; `chunks` is the total. */
  chunk: number;
  chunks: number;
  rate: number;
  mic: MicState;
  /** The microphone is open for a question or a reply. */
  hearing: boolean;
  /** Live transcript while listening, then the question while it is answered. */
  heard: string;
  /** The answer being spoken. */
  answer: string;
  /** The question field has the focus and holds the reading. */
  typing: boolean;
  /** The microphone button is held, or was tapped and is listening. */
  talking: boolean;
  /** Waiting for the voice (a clip that is still being rendered). */
  buffering: boolean;
  engine: VoiceEngine;
  note: NarratorNote;
}

export interface AskRequest {
  question: string;
  history: Array<{ question: string; answer: string }>;
  /** The last chunks read before the interruption. */
  readSoFar: string;
}

export type NarratorLines = Record<NarratorLineKey, string>;

export interface NarratorOptions {
  articleId: string;
  title: string;
  lede: string;
  /** Language of the article ('fr'). */
  language: LanguageType;
  /** Full locale ('fr-CA'): the recognizer's language and the device voice's preferred region. */
  locale: string;
  lines: NarratorLines;
  /** Supabase project URL, to fetch rendered clips straight from storage. */
  storageUrl?: string | null;
  /** The element whose children are the article body. */
  getRoot: () => HTMLElement | null;
  ask: (request: AskRequest, opts: { voice: boolean }) => Promise<ReaderAnswer>;
}

/* Minimal typings for the Web Speech recognition API (not in lib.dom). */
interface RecognitionAlternative {
  transcript: string;
}
interface RecognitionResult extends ArrayLike<RecognitionAlternative> {
  isFinal: boolean;
}
interface RecognitionEvent {
  resultIndex: number;
  results: ArrayLike<RecognitionResult>;
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: RecognitionEvent) => void) | null;
  onerror: ((event: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionCtor = new () => Recognition;

/**
 * push       the microphone button is held
 * question   listening for a question until a silence (a tap, or talking over the reader)
 * followup   "anything else?": waiting a few seconds for a reply
 * finishing  the button was let go: collecting the last words
 */
type ListenMode = 'push' | 'question' | 'followup' | 'finishing';

type Utterance =
  | { kind: 'chunk'; index: number; from: number }
  | { kind: 'line'; key: NarratorLineKey }
  | { kind: 'answer'; text: string; audio: ReadableStream<Uint8Array> | null; sampleRate: number };

/**
 * A question out for its answer, while the reader says it is looking. One
 * per question; any cancel of the reader's speech drops it.
 */
interface Waiting {
  /** A waiting line is being said. */
  speaking: boolean;
  /** Its sound has started, so it is let finish rather than cut mid-word. */
  audible: boolean;
  /** Before "Still looking": a few seconds of quiet after "Let me check". */
  timer: ReturnType<typeof setTimeout> | null;
  /** The answer (or the apology), arrived while the line was audible: given when it ends. */
  held: { deliver: () => void; drop: () => void } | null;
}

/** Quiet after the listener's last words before the question is sent. */
const SILENCE_AFTER_FINAL_MS = 1200;
const SILENCE_AFTER_INTERIM_MS = 2200;
/** Listening for a question that never comes, before reading resumes. */
const QUESTION_DEADLINE_MS = 15_000;
/** Holding the button this long sends what was heard anyway. */
const PUSH_DEADLINE_MS = 60_000;
/** "Anything else?" waits this long for a reply. */
const FOLLOWUP_WINDOW_MS = 6_000;
/** A press shorter than this is a tap: keep listening after the finger lifts. */
export const TAP_MS = 350;
/** After the button is let go, the recognizer's last words arrive within this. */
const FINISH_GRACE_MS = 1_500;
/** A resumed paragraph starts this far before the point it stopped. */
const REWIND_SECONDS = 1.2;
/** A manual scroll holds the page still for this long. */
const USER_SCROLL_HOLD_MS = 6_000;
const NOTE_MS = 8_000;
const MAX_RESTARTS_IN_A_ROW = 6;

const PAUSE_BEFORE_HEADING_MS = { rendered: 350, device: 500 };
const PAUSE_BEFORE_PARAGRAPH_MS = { rendered: 120, device: 220 };
const PAUSE_BETWEEN_SENTENCES_MS = 60;
/** Clips rendered ahead of the one playing. */
const PREFETCH_AHEAD = 3;
/** A clip that does not start within this shows "One moment". */
const BUFFERING_AFTER_MS = 400;
/** Quiet after "Let me check the article." before "Still looking, one moment." */
export const STILL_SEARCHING_AFTER_MS = 5_000;

const DEFAULT_RATE = 1;

export const NARRATOR_RATES = [1, 1.25, 1.5, 0.9] as const;

function recognitionCtor(): RecognitionCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition || w.webkitSpeechRecognition;
}

function hasSynthesis(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';
}

/** The site's locale codes as speech tags: 'en-UK' is 'en-GB' to a browser, 'en-ALL' is 'en'. */
export function speechTag(locale: string): string {
  const [base = '', region = ''] = locale.split('-');
  if (!region || region === 'ALL') return base;
  if (region === 'UK') return `${base}-GB`;
  return `${base}-${region}`;
}

export const SERVER_SNAPSHOT: NarratorSnapshot = {
  available: false,
  phase: 'idle',
  chunk: 0,
  chunks: 0,
  rate: DEFAULT_RATE,
  mic: 'unsupported',
  hearing: false,
  heard: '',
  answer: '',
  typing: false,
  talking: false,
  buffering: false,
  engine: 'rendered',
  note: null,
};

const ACTIVE: ReadonlySet<NarratorPhase> = new Set(['reading', 'paused', 'listening', 'thinking', 'answering']);

export class Narrator {
  private state: NarratorSnapshot = { ...SERVER_SNAPSHOT };
  private listeners = new Set<() => void>();

  private blocks: NarratorBlock<HTMLElement>[] = [];
  private chunks: NarratorChunk[] = [];
  private deviceVoice: SpeechSynthesisVoice | null = null;
  private marked: HTMLElement | null = null;
  private history: AskRequest['history'] = [];

  // Output. Every cancel bumps the sequence, so a stale callback is ignored.
  private speakSeq = 0;
  private speakTimer: ReturnType<typeof setTimeout> | null = null;
  private bufferingTimer: ReturnType<typeof setTimeout> | null = null;
  private current: Utterance | null = null;
  private deviceSentence = 0;
  private recentSpoken: string[] = [];
  private readonly player: ClipPlayer;
  private readonly answerPlayer = new PcmStreamPlayer();
  private source: ClipSource | null = null;
  private renderFailures = 0;
  private waiting: Waiting | null = null;

  // Where the reading stopped inside the current chunk (seconds, or a sentence on the device voice).
  private resumeFrom = 0;
  /** An answer was spoken since the reading stopped: say "Back to the article." before going on. */
  private talkedSinceInterrupt = false;
  /** The question field took the focus while the reading was paused. */
  private typingFromPause = false;
  /** The last question was spoken: listen for the reply to "Anything else?". */
  private lastQuestionSpoken = false;

  // Recognition.
  private rec: Recognition | null = null;
  private recSeq = 0;
  private listenMode: ListenMode | null = null;
  private ignoredResults = new Set<number>();
  private heardText = '';
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private deadlineTimer: ReturnType<typeof setTimeout> | null = null;
  private restartsInARow = 0;
  private lastRestartAt = 0;

  private noteTimer: ReturnType<typeof setTimeout> | null = null;
  private lastUserScroll = 0;
  private askSeq = 0;

  private readonly onVoicesChanged = () => this.loadDeviceVoices();
  private readonly onUserScroll = () => {
    this.lastUserScroll = Date.now();
  };

  private readonly tag: string;
  private attached = false;

  constructor(private readonly opts: NarratorOptions) {
    this.tag = speechTag(opts.locale);
    this.player = new ClipPlayer(() => this.onVoiceStarted());
    if (typeof window === 'undefined') return;
    this.state = {
      ...this.state,
      mic: recognitionCtor() ? 'ready' : 'unsupported',
      engine: HOSTED_VOICE_LANGUAGES.has(opts.language) ? 'rendered' : 'device',
    };
  }

  /** Find the voices and follow their changes. Idempotent; the view calls it on mount. */
  attach() {
    if (this.attached || typeof window === 'undefined') return;
    this.attached = true;
    if (this.state.engine === 'rendered') this.set({ available: true });
    if (hasSynthesis()) {
      this.loadDeviceVoices();
      window.speechSynthesis.addEventListener('voiceschanged', this.onVoicesChanged);
    }
  }

  /** Stop everything and let the voices go. The view calls it on unmount. */
  detach() {
    this.stop();
    if (!this.attached) return;
    this.attached = false;
    if (hasSynthesis()) window.speechSynthesis.removeEventListener('voiceschanged', this.onVoicesChanged);
  }

  /* ───────── store ───────── */

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): NarratorSnapshot => this.state;

  private set(patch: Partial<NarratorSnapshot>) {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  private get active(): boolean {
    return ACTIVE.has(this.state.phase);
  }

  /* ───────── voices ───────── */

  private loadDeviceVoices() {
    if (!hasSynthesis()) return;
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) return;
    const region = this.tag.split('-')[1];
    this.deviceVoice = pickNarratorVoice(voices, this.opts.language, region);
    if (this.state.engine === 'device') this.set({ available: this.deviceVoice !== null });
  }

  /* ───────── public controls ───────── */

  /** Read from the top: the reader's intro line, the title, the lede, the body. Call inside the Listen tap. */
  start() {
    if (!this.state.available || this.active) return;
    // iOS plays sound only from a gesture: unlock both outputs before any await.
    this.player.unlock();
    this.answerPlayer.unlock();
    const rendered = HOSTED_VOICE_LANGUAGES.has(this.opts.language) && this.renderFailures < 2;
    const engine: VoiceEngine = rendered ? 'rendered' : 'device';
    if (engine === 'device' && !this.deviceVoice) {
      this.set({ note: 'voice-failed' });
      this.scheduleNoteClear();
      return;
    }
    const head: NarratorBlock<HTMLElement>[] = [
      { kind: 'intro', text: this.opts.lines.intro },
      { kind: 'title', text: cleanForSpeech(this.opts.title) },
      { kind: 'lede', text: cleanForSpeech(this.opts.lede) },
    ];
    this.blocks = [...head, ...collectArticleBlocks(this.opts.getRoot())].filter((block) => block.text);
    this.chunks =
      engine === 'rendered'
        ? chunkBlocks(this.blocks, MAX_RENDERED_CHUNK_CHARS, true)
        : chunkBlocks(this.blocks, MAX_CHUNK_CHARS);
    this.history = [];
    this.talkedSinceInterrupt = false;
    this.source?.release();
    this.source =
      engine === 'rendered'
        ? new ClipSource({ lang: this.opts.language, articleId: this.opts.articleId, storageUrl: this.opts.storageUrl })
        : null;
    if (this.source) {
      for (const key of ['backToArticle', 'end', 'searching', 'stillSearching'] as const) {
        this.source.prefetch(this.lineRequest(key));
      }
    }
    this.unwatchScroll();
    window.addEventListener('wheel', this.onUserScroll, { passive: true });
    window.addEventListener('touchmove', this.onUserScroll, { passive: true });
    window.addEventListener('keydown', this.onUserScroll);
    this.setUpMediaSession();
    this.set({ engine, chunk: 0, chunks: this.chunks.length, heard: '', answer: '', typing: false, talking: false, note: null });
    this.readFrom(0, 0);
  }

  pause() {
    if (!this.active || this.state.phase === 'paused') return;
    // Pausing during a question drops the question; the place in the article is kept.
    this.askSeq++;
    this.interruptSpeech();
    this.stopListening();
    this.set({ phase: 'paused', heard: '', answer: '', typing: false, talking: false });
  }

  resume() {
    if (this.state.phase !== 'paused') return;
    this.player.unlock();
    this.answerPlayer.unlock();
    this.readFrom(this.state.chunk, this.resumeFrom);
  }

  togglePlay() {
    if (this.state.phase === 'paused') this.resume();
    else if (!this.active) this.start();
    else this.pause();
  }

  stop() {
    this.askSeq++;
    this.cancelSpeech();
    this.stopListening();
    this.mark(null);
    this.clearNote();
    this.unwatchScroll();
    this.tearDownMediaSession();
    this.source?.release();
    this.source = null;
    this.history = [];
    this.resumeFrom = 0;
    this.set({ phase: 'idle', chunk: 0, heard: '', answer: '', typing: false, talking: false, buffering: false });
  }

  setRate(rate: number) {
    this.set({ rate });
    if (this.state.engine === 'rendered') {
      this.player.setRate(rate);
      return;
    }
    if (this.state.phase === 'reading' && this.current?.kind === 'chunk') {
      const from = this.deviceSentence;
      this.cancelSpeech();
      this.readCurrent(from);
    }
  }

  cycleRate() {
    const at = NARRATOR_RATES.indexOf(this.state.rate as (typeof NARRATOR_RATES)[number]);
    this.setRate(NARRATOR_RATES[(at + 1) % NARRATOR_RATES.length]);
  }

  /* ───────── push to talk ───────── */

  /** The microphone button went down: stop speaking and listen. Call inside the gesture. */
  talkStart() {
    if (!this.active || this.state.mic !== 'ready') return;
    this.answerPlayer.unlock();
    this.askSeq++;
    this.interruptSpeech();
    this.stopListening();
    this.typingFromPause = false;
    this.set({ phase: 'listening', heard: '', answer: '', typing: false, talking: true, buffering: false });
    this.startListening('push');
  }

  /** The button came up after `heldMs`: a tap keeps listening until a silence, a hold sends now. */
  talkRelease(heldMs: number) {
    if (this.listenMode !== 'push') return;
    if (heldMs < TAP_MS) {
      this.listenMode = 'question';
      this.armDeadline(QUESTION_DEADLINE_MS, () => this.onQuestionDeadline());
      if (this.heardText) this.armSilence(SILENCE_AFTER_INTERIM_MS);
      return;
    }
    this.finishTalking();
  }

  /** Send what was heard (a second tap, or the button let go). */
  finishTalking() {
    if (!this.state.talking && this.listenMode !== 'push' && this.listenMode !== 'question') return;
    this.set({ talking: false });
    const rec = this.rec;
    if (!rec) {
      this.afterTalk();
      return;
    }
    this.listenMode = 'finishing';
    this.clearTimers();
    try {
      rec.stop();
    } catch {
      this.afterTalk();
      return;
    }
    this.armDeadline(FINISH_GRACE_MS, () => this.afterTalk());
  }

  private afterTalk() {
    const text = this.heardText;
    this.stopListening();
    this.set({ talking: false });
    if (text) this.submitQuestion(text, { spoken: true });
    else this.continueReading();
  }

  /* ───────── the question field ───────── */

  /** The question field took the focus: hold the reading. */
  holdForTyping() {
    if (!this.active || this.state.typing) return;
    if (this.state.phase === 'thinking') {
      // The question stays asked; only "Let me check" goes quiet.
      this.hushWaiting();
      return;
    }
    this.typingFromPause = this.state.phase === 'paused';
    this.interruptSpeech();
    this.stopListening();
    this.clearTimers();
    this.set({ phase: 'listening', typing: true, talking: false, heard: '' });
  }

  /** The field lost the focus with nothing in it: go on as before. */
  releaseTyping() {
    if (!this.state.typing) return;
    this.set({ typing: false });
    if (this.typingFromPause) {
      this.typingFromPause = false;
      this.set({ phase: 'paused' });
      return;
    }
    this.continueReading();
  }

  /** The listener is typing: "Anything else?" stops counting down. */
  typingActivity() {
    this.clearTimers();
  }

  /** A typed question, or a spoken one. A new question replaces one still waiting for its answer. */
  submitQuestion(question: string, opts: { spoken?: boolean } = {}) {
    const text = cleanForSpeech(question);
    if (!text || !this.active) return;
    this.lastQuestionSpoken = Boolean(opts.spoken);
    this.answerPlayer.unlock();
    // Whatever is speaking stops, "Let me check" of a question this one replaces included.
    this.interruptSpeech();
    this.stopListening();
    this.typingFromPause = false;
    this.set({ phase: 'thinking', heard: text, answer: '', typing: false, talking: false, buffering: false });
    const seq = ++this.askSeq;
    this.sayWhileWaiting((this.waiting = { speaking: false, audible: false, timer: null, held: null }), 'searching');
    const at = this.state.chunk;
    const readSoFar = this.chunks
      .slice(Math.max(0, at - 2), at + 1)
      .map((c) => c.text)
      .join(' ');
    this.opts
      .ask({ question: text, history: this.history.slice(-4), readSoFar }, { voice: this.state.engine === 'rendered' })
      .then((response) => {
        const dropAudio = () => {
          response.audio?.cancel().catch(() => undefined);
        };
        if (seq !== this.askSeq) {
          dropAudio();
          return;
        }
        this.whenDoneWaiting(() => {
          const answer = cleanForSpeech(response.answer);
          if (response.done || !answer) {
            dropAudio();
            this.talkedSinceInterrupt = true;
            this.continueReading();
            return;
          }
          this.history.push({ question: text, answer });
          this.speakAnswer(answer, response.audio, response.sampleRate);
        }, dropAudio);
      })
      .catch((error) => {
        if (seq !== this.askSeq) return;
        console.error('[narrator] ask failed:', error);
        this.whenDoneWaiting(() => {
          this.set({ phase: 'answering', answer: '', note: 'answer-failed' });
          this.scheduleNoteClear();
          // The line itself ends with "Back to the article."
          this.say({ kind: 'line', key: 'answerFailed' }, () => this.resumeReading());
        });
      });
  }

  /* ───────── while a question is out ───────── */

  /**
   * Say a waiting line. When it ends, an answer that arrived meanwhile is
   * spoken; otherwise "Let me check" is followed, after a few quiet seconds,
   * by one "Still looking", and then nothing more.
   */
  private sayWhileWaiting(w: Waiting, key: 'searching' | 'stillSearching') {
    w.speaking = true;
    w.audible = false;
    this.say({ kind: 'line', key }, () => {
      if (this.waiting !== w) return;
      w.speaking = false;
      w.audible = false;
      if (w.held) {
        const { deliver } = w.held;
        this.waiting = null;
        deliver();
        return;
      }
      if (key === 'stillSearching') {
        this.waiting = null;
        return;
      }
      w.timer = setTimeout(() => {
        w.timer = null;
        if (this.waiting === w && this.state.phase === 'thinking') this.sayWhileWaiting(w, 'stillSearching');
      }, STILL_SEARCHING_AFTER_MS);
    });
  }

  /**
   * The answer (or the apology) is in. A waiting line already heard is let
   * finish first; one still loading, or still to come, is dropped.
   */
  private whenDoneWaiting(deliver: () => void, drop: () => void = () => undefined) {
    const w = this.waiting;
    if (w?.speaking && w.audible) {
      w.held = { deliver, drop };
      return;
    }
    if (w?.speaking) this.cancelSpeech();
    else this.dropWaiting();
    deliver();
  }

  /** The question field took the focus while a question is out: quiet now, the answer still comes. */
  private hushWaiting() {
    const w = this.waiting;
    if (!w) return;
    const held = w.held;
    w.held = null;
    if (w.speaking) this.cancelSpeech();
    else this.dropWaiting();
    held?.deliver();
  }

  private dropWaiting() {
    const w = this.waiting;
    if (!w) return;
    this.waiting = null;
    if (w.timer) clearTimeout(w.timer);
    w.held?.drop();
  }

  /** Close the question (the ✕ while listening or thinking) and carry on reading. */
  cancelAsk() {
    if (this.state.phase !== 'listening' && this.state.phase !== 'thinking') return;
    this.askSeq++;
    this.cancelSpeech();
    this.stopListening();
    this.set({ typing: false, talking: false });
    this.continueReading();
  }

  /* ───────── reading ───────── */

  private readFrom(index: number, from: number) {
    this.talkedSinceInterrupt = false;
    this.set({ phase: 'reading', chunk: index, heard: '', answer: '', typing: false, talking: false });
    this.readCurrent(from);
  }

  private readCurrent(from: number) {
    const i = this.state.chunk;
    if (i >= this.chunks.length) {
      this.finish();
      return;
    }
    const chunk = this.chunks[i];
    const block = this.blocks[chunk.block];
    this.mark(block.el ?? null);
    for (let j = i + 1; j <= i + PREFETCH_AHEAD && j < this.chunks.length; j++) this.source?.prefetch(this.chunkRequest(j));
    const engine = this.state.engine;
    const previous = i > 0 ? this.chunks[i - 1] : null;
    const pauseBefore =
      i === 0 || from > 0
        ? 0
        : block.kind === 'heading'
          ? PAUSE_BEFORE_HEADING_MS[engine]
          : previous && previous.block !== chunk.block
            ? PAUSE_BEFORE_PARAGRAPH_MS[engine]
            : engine === 'device'
              ? PAUSE_BETWEEN_SENTENCES_MS
              : 0;
    this.after(pauseBefore, () =>
      this.say({ kind: 'chunk', index: i, from }, () => {
        if (this.state.phase !== 'reading' || this.state.chunk !== i) return;
        this.resumeFrom = 0;
        this.set({ chunk: i + 1 });
        this.readCurrent(0);
      }),
    );
  }

  private finish() {
    this.mark(null);
    this.stopListening();
    this.say({ kind: 'line', key: 'end' }, () => {
      this.tearDownMediaSession();
      this.set({ phase: 'ended', chunk: this.chunks.length });
    });
  }

  /** Back to the article where it stopped, with "Back to the article." after a conversation. */
  private continueReading() {
    this.stopListening();
    this.clearTimers();
    if (!this.talkedSinceInterrupt) {
      this.resumeReading();
      return;
    }
    this.set({ phase: 'answering', heard: '', answer: '', typing: false, talking: false });
    this.say({ kind: 'line', key: 'backToArticle' }, () => this.resumeReading());
  }

  private resumeReading() {
    this.readFrom(this.state.chunk, this.resumeFrom);
  }

  private speakAnswer(answer: string, audio: ReadableStream<Uint8Array> | null, sampleRate: number) {
    this.talkedSinceInterrupt = true;
    this.set({ phase: 'answering', answer, typing: false, talking: false });
    this.say({ kind: 'answer', text: answer, audio, sampleRate }, () => this.afterAnswer());
  }

  /**
   * The answer closed with "Anything else?": a few seconds for a reply, then
   * the article. A spoken question gets a listened-for reply (the reader is
   * silent by then); a typed one waits for the field.
   */
  private afterAnswer() {
    this.set({ phase: 'listening', heard: '' });
    if (this.lastQuestionSpoken && this.state.mic === 'ready') {
      this.startListening('followup');
      return;
    }
    this.armDeadline(FOLLOWUP_WINDOW_MS, () => this.continueReading());
  }

  /* ───────── output ───────── */

  private chunkRequest(index: number): ClipRequest {
    const chunk = this.chunks[index];
    if (this.blocks[chunk.block]?.kind === 'intro') return this.lineRequest('intro');
    return { kind: 'article', text: chunk.text };
  }

  private lineRequest(key: NarratorLineKey): ClipRequest {
    return { kind: 'line', line: key, text: this.opts.lines[key] };
  }

  private textOf(u: Utterance): string {
    if (u.kind === 'chunk') return this.chunks[u.index]?.text ?? '';
    if (u.kind === 'line') return this.opts.lines[u.key];
    return u.text;
  }

  private after(ms: number, fn: () => void) {
    if (ms <= 0) {
      fn();
      return;
    }
    const seq = this.speakSeq;
    this.speakTimer = setTimeout(() => {
      this.speakTimer = null;
      if (seq === this.speakSeq) fn();
    }, ms);
  }

  /** Say one utterance with the session's voice; `onEnd` runs only if nothing cut it. */
  private say(u: Utterance, onEnd: () => void, attempt: number = 0) {
    this.current = u;
    const text = this.textOf(u);
    this.recentSpoken = [...this.recentSpoken.slice(-1), text];
    if (this.state.engine === 'device') {
      const from = u.kind === 'chunk' ? Math.floor(u.from) : 0;
      this.deviceSay(text, from, onEnd);
      return;
    }
    const seq = this.speakSeq;
    const fail = (error: unknown) => {
      if (seq !== this.speakSeq) return;
      this.clearBuffering();
      console.warn('[narrator] rendered voice failed:', error instanceof Error ? error.message : error);
      if (u.kind !== 'answer' && attempt === 0) {
        const req = u.kind === 'chunk' ? this.chunkRequest(u.index) : this.lineRequest(u.key);
        this.source?.forget(req);
        this.say(u, onEnd, 1);
        return;
      }
      this.onRenderedVoiceFailed(u, onEnd);
    };

    if (u.kind === 'answer') {
      if (!u.audio) {
        this.onRenderedVoiceFailed(u, onEnd);
        return;
      }
      this.answerPlayer
        .play(u.audio, u.sampleRate, () => this.clearBuffering())
        .then((result) => {
          if (seq !== this.speakSeq) return;
          this.clearBuffering();
          if (result === 'ended') onEnd();
          else if (result === 'empty') this.onRenderedVoiceFailed(u, onEnd);
        })
        .catch(fail);
      this.armBuffering(seq);
      return;
    }

    const req = u.kind === 'chunk' ? this.chunkRequest(u.index) : this.lineRequest(u.key);
    const from = u.kind === 'chunk' ? u.from : 0;
    this.armBuffering(seq);
    this.source!
      .get(req)
      .then((url) => {
        if (seq !== this.speakSeq) return null;
        return this.player.play(url, { rate: this.state.rate, from });
      })
      .then((result) => {
        if (result === 'ended' && seq === this.speakSeq) {
          this.renderFailures = 0;
          onEnd();
        }
      })
      .catch(fail);
  }

  /**
   * A clip failed twice (or an answer came without sound). That one piece is
   * said by the device's voice when there is one; two clips failing in a row
   * hand the rest of the session to the device's voice. With no device voice
   * the piece is skipped (an answer stays on screen long enough to read), and
   * three failures in a row hold the reading with a note.
   */
  private onRenderedVoiceFailed(u: Utterance, onEnd: () => void) {
    const text = this.textOf(u);
    if (u.kind !== 'answer') this.renderFailures++;
    if (this.deviceVoice) {
      if (this.renderFailures >= 2) this.set({ engine: 'device' });
      this.deviceSay(text, 0, onEnd);
      return;
    }
    if (u.kind === 'answer') {
      this.after(Math.min(8_000, 1_500 + text.length * 40), onEnd);
      return;
    }
    if (this.renderFailures >= 3) {
      this.set({ note: 'voice-failed' });
      this.scheduleNoteClear();
      this.pause();
      return;
    }
    onEnd();
  }

  /** The device's voice, a sentence at a time (Chrome cuts any utterance over about fifteen seconds). */
  private deviceSay(text: string, fromSentence: number, onEnd: () => void) {
    if (!hasSynthesis()) {
      onEnd();
      return;
    }
    const seq = this.speakSeq;
    const sentences = splitSentences(text, MAX_CHUNK_CHARS);
    const next = (k: number) => {
      if (seq !== this.speakSeq) return;
      if (k >= sentences.length) {
        onEnd();
        return;
      }
      this.deviceSentence = k;
      const utterance = new SpeechSynthesisUtterance(sentences[k]);
      if (this.deviceVoice) utterance.voice = this.deviceVoice;
      utterance.lang = this.deviceVoice?.lang || this.tag;
      utterance.rate = this.state.rate;
      utterance.pitch = 1;
      utterance.volume = 1;
      let settled = false;
      const settle = () => {
        if (settled || seq !== this.speakSeq) return;
        settled = true;
        this.after(k + 1 < sentences.length ? PAUSE_BETWEEN_SENTENCES_MS : 0, () => next(k + 1));
      };
      utterance.onend = settle;
      utterance.onerror = (event) => {
        // Our own cancel; the sequence guard already drops the callback.
        if (event.error === 'interrupted' || event.error === 'canceled') return;
        console.warn('[narrator] utterance error:', event.error);
        settle();
      };
      const synth = window.speechSynthesis;
      // Chrome keeps a cancelled queue in a paused state now and then.
      if (synth.paused) synth.resume();
      synth.speak(utterance);
      this.onVoiceStarted();
    };
    next(Math.min(Math.max(0, fromSentence), Math.max(0, sentences.length - 1)));
  }

  /** Stop speaking, remembering where the article was if it was the article speaking. */
  private interruptSpeech() {
    if (this.current?.kind === 'chunk') {
      this.resumeFrom =
        this.state.engine === 'rendered' ? Math.max(0, this.player.position() - REWIND_SECONDS) : this.deviceSentence;
    }
    this.cancelSpeech();
  }

  private cancelSpeech() {
    this.speakSeq++;
    if (this.speakTimer) {
      clearTimeout(this.speakTimer);
      this.speakTimer = null;
    }
    // "Let me check" goes like any other speech, with an answer held for its end.
    this.dropWaiting();
    this.current = null;
    this.player.stop();
    this.answerPlayer.stop();
    if (hasSynthesis()) window.speechSynthesis.cancel();
    this.clearBuffering();
  }

  /** Sound is coming out. A waiting line that has started is now let finish. */
  private onVoiceStarted() {
    this.clearBuffering();
    if (this.waiting?.speaking) this.waiting.audible = true;
  }

  private armBuffering(seq: number) {
    if (this.bufferingTimer) clearTimeout(this.bufferingTimer);
    this.bufferingTimer = setTimeout(() => {
      this.bufferingTimer = null;
      if (seq === this.speakSeq) this.set({ buffering: true });
    }, BUFFERING_AFTER_MS);
  }

  private clearBuffering() {
    if (this.bufferingTimer) clearTimeout(this.bufferingTimer);
    this.bufferingTimer = null;
    if (this.state.buffering) this.set({ buffering: false });
  }

  /* ───────── lock screen and headphone controls ───────── */

  private setUpMediaSession() {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      if (typeof MediaMetadata !== 'undefined') {
        navigator.mediaSession.metadata = new MediaMetadata({ title: this.opts.title, artist: 'Inception' });
      }
      navigator.mediaSession.setActionHandler('play', () => this.resume());
      navigator.mediaSession.setActionHandler('pause', () => this.pause());
      navigator.mediaSession.setActionHandler('stop', () => this.stop());
    } catch {
      // Unsupported action: the page controls still work.
    }
  }

  private tearDownMediaSession() {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.metadata = null;
      for (const action of ['play', 'pause', 'stop'] as const) navigator.mediaSession.setActionHandler(action, null);
    } catch {
      // Nothing to clear.
    }
  }

  private unwatchScroll() {
    if (typeof window === 'undefined') return;
    window.removeEventListener('wheel', this.onUserScroll);
    window.removeEventListener('touchmove', this.onUserScroll);
    window.removeEventListener('keydown', this.onUserScroll);
  }

  /* ───────── marking the page ───────── */

  private mark(el: HTMLElement | null) {
    if (this.marked && this.marked !== el) this.marked.removeAttribute('data-narrator-current');
    if (el && this.marked !== el) {
      el.setAttribute('data-narrator-current', '');
      this.follow(el);
    }
    this.marked = el;
  }

  private follow(el: HTMLElement) {
    if (Date.now() - this.lastUserScroll < USER_SCROLL_HOLD_MS) return;
    if (typeof el.getBoundingClientRect !== 'function') return;
    const rect = el.getBoundingClientRect();
    const height = window.innerHeight || 0;
    if (rect.top < height * 0.12 || rect.bottom > height * 0.7) {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  /* ───────── speech recognition ───────── */

  private newRecognizer(): Recognition | null {
    const Ctor = recognitionCtor();
    if (!Ctor) return null;
    const rec = new Ctor();
    rec.lang = this.tag;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    return rec;
  }

  private wire(rec: Recognition, carried: string) {
    const seq = ++this.recSeq;
    rec.onresult = (event) => {
      if (seq === this.recSeq) this.onRecognitionResult(event, carried);
    };
    rec.onerror = (event) => {
      if (seq === this.recSeq) this.onRecognitionError(event.error);
    };
    rec.onend = () => {
      if (seq === this.recSeq) this.onRecognitionEnd();
    };
  }

  private startListening(mode: ListenMode) {
    if (this.state.mic !== 'ready') {
      if (mode === 'followup') this.armDeadline(FOLLOWUP_WINDOW_MS, () => this.continueReading());
      return;
    }
    this.stopListening();
    const rec = this.newRecognizer();
    if (!rec) return;
    this.listenMode = mode;
    this.ignoredResults = new Set();
    this.heardText = '';
    this.wire(rec, '');
    this.rec = rec;
    try {
      rec.start();
    } catch (error) {
      console.warn('[narrator] recognition did not start:', error);
      this.rec = null;
      this.recSeq++;
      this.listenMode = null;
      if (mode === 'push' || mode === 'question') this.micUnusable();
      else if (mode === 'followup') this.continueReading();
      return;
    }
    this.set({ hearing: true });
    if (mode === 'push') this.armDeadline(PUSH_DEADLINE_MS, () => this.finishTalking());
    if (mode === 'question') this.armDeadline(QUESTION_DEADLINE_MS, () => this.onQuestionDeadline());
    if (mode === 'followup') this.armDeadline(FOLLOWUP_WINDOW_MS, () => this.continueReading());
  }

  private stopListening() {
    const rec = this.rec;
    this.rec = null;
    this.recSeq++;
    this.listenMode = null;
    this.clearTimers();
    if (this.state.hearing) this.set({ hearing: false });
    if (!rec) return;
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
    try {
      rec.abort();
    } catch {
      // Already stopped.
    }
  }

  /** Chrome ends continuous recognition on its own now and then: start again, keeping what was heard. */
  private restartListening() {
    const mode = this.listenMode;
    if (!mode) return;
    const now = Date.now();
    this.restartsInARow = now - this.lastRestartAt < 2000 ? this.restartsInARow + 1 : 0;
    this.lastRestartAt = now;
    if (this.restartsInARow > MAX_RESTARTS_IN_A_ROW) {
      console.warn('[narrator] recognition keeps ending; giving up on this question');
      this.giveUpListening(mode);
      return;
    }
    const heard = this.heardText;
    const old = this.rec;
    if (old) {
      old.onresult = null;
      old.onerror = null;
      old.onend = null;
    }
    const next = this.newRecognizer();
    if (!next) return;
    this.ignoredResults = new Set();
    this.heardText = heard;
    this.wire(next, heard);
    this.rec = next;
    try {
      next.start();
    } catch {
      this.giveUpListening(mode);
    }
  }

  private giveUpListening(mode: ListenMode) {
    this.rec = null;
    this.recSeq++;
    this.listenMode = null;
    this.set({ hearing: false });
    if (mode === 'push' || mode === 'question' || mode === 'finishing') this.afterTalk();
    else if (mode === 'followup') this.continueReading();
  }

  /** The microphone cannot be used: wait for a typed question, then read on. */
  private micUnusable() {
    this.set({ talking: false });
    this.armDeadline(QUESTION_DEADLINE_MS, () => this.continueReading());
  }

  private onRecognitionResult(event: RecognitionEvent, carried: string) {
    const mode = this.listenMode;
    if (!mode) return;
    const results = event.results;
    const spoken = this.recentSpoken.join(' ');
    // The reply window opens as the answer ends: its tail can still be in the room.
    const guardEcho = mode === 'followup';
    for (let k = event.resultIndex; k < results.length; k++) {
      const segment = results[k]?.[0]?.transcript ?? '';
      if (guardEcho && looksLikeEcho(segment, spoken)) this.ignoredResults.add(k);
      else this.ignoredResults.delete(k);
    }
    let text = carried ? `${carried} ` : '';
    let lastIsFinal = false;
    for (let k = 0; k < results.length; k++) {
      if (this.ignoredResults.has(k)) continue;
      text += `${results[k]?.[0]?.transcript ?? ''} `;
      lastIsFinal = Boolean(results[k]?.isFinal);
    }
    text = cleanForSpeech(text);
    if (mode === 'followup') {
      if (speechWords(text).length === 0) return;
      this.listenMode = 'question';
      this.clearTimers();
      this.armDeadline(QUESTION_DEADLINE_MS, () => this.onQuestionDeadline());
    }
    this.heardText = text;
    this.set({ heard: text });
    // Held or let go: the button decides when the question is complete.
    if (this.listenMode === 'question') this.armSilence(lastIsFinal ? SILENCE_AFTER_FINAL_MS : SILENCE_AFTER_INTERIM_MS);
  }

  private onRecognitionError(error: string) {
    const mode = this.listenMode;
    if (error === 'aborted' || error === 'no-speech') return;
    if (error === 'not-allowed' || error === 'service-not-allowed' || error === 'audio-capture') {
      this.rec = null;
      this.recSeq++;
      this.listenMode = null;
      this.clearTimers();
      this.set({ mic: 'blocked', hearing: false, talking: false, note: 'mic-blocked' });
      this.scheduleNoteClear();
      if (mode === 'push' || mode === 'question' || mode === 'finishing') this.micUnusable();
      else if (mode === 'followup') this.continueReading();
      return;
    }
    if (error === 'network') {
      // Chrome's recognizer needs the network; this session types instead.
      this.rec = null;
      this.recSeq++;
      this.listenMode = null;
      this.clearTimers();
      this.set({ hearing: false, talking: false });
      if (mode === 'push' || mode === 'question' || mode === 'finishing') this.afterTalk();
      else if (mode === 'followup') this.continueReading();
    }
  }

  private onRecognitionEnd() {
    const mode = this.listenMode;
    if (!mode) return;
    if (mode === 'finishing') {
      this.afterTalk();
      return;
    }
    if (mode === 'question' && this.heardText) {
      this.submitQuestion(this.heardText, { spoken: true });
      return;
    }
    // push, followup, or a question with nothing heard yet: the timers decide; keep listening.
    this.restartListening();
  }

  private onQuestionDeadline() {
    if (this.state.phase !== 'listening') return;
    if (this.heardText) this.submitQuestion(this.heardText, { spoken: true });
    else {
      this.set({ talking: false });
      this.continueReading();
    }
  }

  private armSilence(ms: number) {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      this.silenceTimer = null;
      if (this.state.phase !== 'listening') return;
      if (this.heardText) this.submitQuestion(this.heardText, { spoken: true });
    }, ms);
  }

  private armDeadline(ms: number, then: () => void) {
    if (this.deadlineTimer) clearTimeout(this.deadlineTimer);
    this.deadlineTimer = setTimeout(() => {
      this.deadlineTimer = null;
      then();
    }, ms);
  }

  private clearTimers() {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    if (this.deadlineTimer) clearTimeout(this.deadlineTimer);
    this.silenceTimer = null;
    this.deadlineTimer = null;
  }

  private scheduleNoteClear() {
    if (this.noteTimer) clearTimeout(this.noteTimer);
    this.noteTimer = setTimeout(() => {
      this.noteTimer = null;
      this.set({ note: null });
    }, NOTE_MS);
  }

  private clearNote() {
    if (this.noteTimer) clearTimeout(this.noteTimer);
    this.noteTimer = null;
    if (this.state.note) this.set({ note: null });
  }
}
